import React, { PropsWithChildren } from 'react';

import { Box } from '@opendoor/bricks-next';
import { ThemedLite } from '@opendoor/bricks/theme/ODThemeLite';
import Head from 'next/head';

import Header from 'components/landing-pages-v2/shared/header/Header';
import { formatStreetAddress, isBuyDirectEligible } from 'components/marketplace/helpers';
import { PropertyDetailsType } from 'components/marketplace/property';
import { getSeoFields } from 'components/properties/helpers/property';
import SeoHead from 'components/properties/shared/SeoHead';
import GlobalStyles from 'components/shared/GlobalStyles';

const mainNavLinks = [
  {
    name: 'How it works',
    href: '/how-it-works-sell',
  },
  {
    name: 'Browse homes',
    href: '/homes',
  },
];

const menuPanelNavLinks = [
  {
    name: 'Work with a builder',
    href: '/builder',
  },
  {
    name: 'Opendoor for Agents',
    href: '/agents',
  },
];

const MarketplaceLayout: React.FC<PropsWithChildren<{ property?: PropertyDetailsType }>> = ({
  children,
  property,
}) => {
  const { coverImage, readableAddress, title, canonicalUrl } = getSeoFields(
    property?.addressToken,
    property?.slug,
    undefined, // status is different
    property?.street,
    property?.unitType,
    property?.unitNumber,
    property?.city,
    property?.state,
    property?.zip,
    property?.sqFtLot,
    property?.bedrooms,
    property?.bathrooms,
    property?.yearBuilt,
    property?.photos?.[0],
  );
  const metaDescription = `View photos, details, and schedule a self-tour of this ${property?.bedrooms} bedroom, ${property?.bathrooms} bathroom, ${property?.sqFtTotalLiving} sqft home located at ${readableAddress}.`;
  const seoHead = (
    <SeoHead
      base={{
        title,
        author: 'Opendoor, Inc.',
        metaDescription,
        canonicalUrl,
      }}
      og={{
        url: canonicalUrl,
        title,
        description: metaDescription,
        siteName: 'Opendoor',
        image: coverImage,
        type: 'website',
      }}
      schema={{
        name: readableAddress,
        description: metaDescription,
        url: canonicalUrl,
        address: {
          ...(property?.street && {
            streetAddress:
              formatStreetAddress(property.street, property.unitType, property.unitNumber) ||
              undefined,
          }),
          ...(property?.city && { addressLocality: property.city }),
          ...(property?.state && { addressRegion: property.state }),
          ...(property?.zip && { postalCode: property.zip }),
        },
        ...(property?.sqFtLot && {
          floorSize: {
            value: property.sqFtLot.toString(),
          },
        }),
        ...(property?.yearBuilt && {
          yearBuilt: property.yearBuilt.toString(),
        }),
        ...(property?.bedrooms && {
          numberOfBedrooms: property.bedrooms.toString(),
        }),
        ...(property?.bathrooms && {
          numberOfBathroomsTotal: property.bathrooms.toString(),
        }),
        ...(property?.listPrice &&
          !isBuyDirectEligible(property) && {
            listPrice: property.listPrice,
          }),
        ...(property?.buyDirectPrice &&
          isBuyDirectEligible(property) && {
            listPrice: property.buyDirectPrice,
          }),
        image: coverImage,
        lat: property?.latitude,
        lon: property?.longitude,
        ...(property?.canSelfTour && { hasSelfTour: property?.canSelfTour }),
        addressToken: property?.addressToken,
      }}
    />
  );
  return (
    <ThemedLite
      baseTheme="novo"
      team="marketplace"
      launchDateString={new Date().toISOString()}
      overrides={{}}
    >
      <Head>
        <link rel="preconnect" href="https://images.opendoor.com" />
        <link rel="dns-prefetch" href="https://images.opendoor.com" />
        <link rel="preconnect" href="https://cdn-cosmos.opendoor.com" />
        <link rel="dns-prefetch" href="https://cdn-cosmos.opendoor.com" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <meta httpEquiv="Accept-CH" content="DPR, Width, Viewport-Width" />
        <link
          rel="apple-touch-icon"
          sizes="144x144"
          href="https://www.opendoor.com/apple-touch-icon-144.png"
        />
      </Head>
      {seoHead}
      <GlobalStyles fontWeights={['regular', 'semibold', 'bold', 'medium']} />
      <Box flexDirection="column" backgroundColor="$backgroundPrimary">
        <Box display="block" width="100%" zIndex={2} style={{ position: 'fixed' }}>
          <Header slug="homes" mainNavLinks={mainNavLinks} menuPanelNavLinks={menuPanelNavLinks} />
        </Box>
        <Box
          tag="main"
          tabIndex={-1}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          overflowX="hidden"
          backgroundColor="$backgroundPrimary"
        >
          {children}
        </Box>
      </Box>
    </ThemedLite>
  );
};

export default MarketplaceLayout;
